/**
 * Ponte com o widget do Huggy (js.huggy.chat/widget.min.js).
 * O objeto `window.Huggy` muda de versão pra versão, então tudo aqui é
 * "feature-detect": se o método não existir, ignora sem quebrar.
 */
import { attributionParams, attributionSummary } from "./attribution";

type HuggyAPI = {
  init?: (opts: Record<string, unknown>) => void;
  open?: () => void;
  openChat?: () => void;
  openBox?: () => void;
  show?: () => void;
  sendMessage?: (text: string) => void;
  setData?: (data: Record<string, unknown>) => void;
  setContext?: (obj: Record<string, unknown>) => void;
};

function api(): HuggyAPI | undefined {
  return (window as unknown as { Huggy?: HuggyAPI }).Huggy;
}

function opener(h: HuggyAPI | undefined): (() => void) | undefined {
  const fn = h && (h.openChat || h.open || h.openBox || h.show);
  return fn ? fn.bind(h) : undefined;
}
/**
 * Pronto quando o stub inicial (só `init`) foi substituído pelo objeto real,
 * que expõe `openChat`. Não há evento "ready" nessa versão do widget.
 */
export function isHuggyReady(): boolean {
  return !!opener(api());
}

/** Campos que o Huggy anexa ao contato (crie-os iguais em Configurações → Campos personalizados). */
export function huggyContextFields(): Record<string, string> {
  const p = attributionParams();
  return {
    origem_site: attributionSummary(),
    campanha: p.utm_campaign || "",
    canal: [p.utm_source, p.utm_medium].filter(Boolean).join(" / "),
    gclid: p.gclid || p.wbraid || p.gbraid || "",
    pagina_entrada: p.landing_page || "",
    sid: p.ci_sid || "",
  };
}

export function pushHuggyContext() {
  const h = api();
  if (!h) return;
  const f = huggyContextFields();
  try {
    h.setContext?.(f);
  } catch {
    /* noop */
  }
  try {
    // versão atual do widget: setData(objeto) -> changeData no contato
    h.setData?.(f);
  } catch {
    /* noop */
  }
}

/** Abre o chat do Huggy. Retorna false se não conseguiu (aí o caller cai no wa.me). */
export function openHuggy(message?: string): boolean {
  const h = api();
  const open = opener(h);
  if (!h || !open) return false;
  pushHuggyContext();
  try {
    open();
  } catch {
    return false;
  }
  if (message && h.sendMessage) {
    setTimeout(() => {
      try {
        h.sendMessage?.(message);
      } catch {
        /* noop */
      }
    }, 1400);
  }
  return true;
}
